import { useCallback, useEffect, useRef, useState } from "react";
import { ClearOutlined, FilterOutlined, SearchOutlined } from "@ant-design/icons";
import { Button, Card, Col, Input, Row, Space, Tag, Typography } from "antd";
import { debounce } from "lodash";
import CommonForm from "@/Components/Common/CommonForm";
import { makeAdvanceFilterLabel } from "../../../Utils/Utils";

const { Search } = Input;
const { Text } = Typography;

export default function CommonFilter(props) {
  const {
    filterFormFields,
    filters = [],
    setFilters,
    setPagination,
    searchKey = "",
    pinnedFilter = [],
    currentTabData,
  } = props;

  const initialPagination = { page: 1, pageSize: 10 };
  const formRef = useRef();
  const [queryValue, setQueryValue] = useState("");
  const [appliedFilters, setAppliedFilters] = useState([]);

  useEffect(() => {
    const foundSearch = filters.find((e) => e.key === "searchQuery");
    setQueryValue(foundSearch?.value || "");
  }, [filters]);

  const handleFiltersQueryChange = useCallback(
    (value) => {
      const filterObj = {
        filterType: "searchQuery",
        nestedKey: searchKey,
      };
      if (value) handleFilterChange({ searchQuery: value }, filterObj); // add searchQuery in filter
      else handleFilterRemove("searchQuery"); // remove searchQuery from array
      setQueryValue(value);
    },
    [filters]
  );

  const handleQueryValueRemove = useCallback(() => setQueryValue(""), []);

  const handleFiltersClearAll = useCallback(() => {
    const searchQueryFilter = filters?.filter((e) => e?.tabQuery || e?.key === "searchQuery");
    if (searchQueryFilter) {
      setFilters([...searchQueryFilter]); //remain search query when filter clear
    } else {
      setFilters([]);
    }
  }, [filters]);

  //call from common form and push that value in filter array
  const handleFilterChange = useCallback(
    debounce((changedFilter, initialValue) => {
      const { filterType, nestedKey, label, dynamicLabel } = initialValue;
      const [key, value] = Object.entries(changedFilter)[0]; //separate key and value from object
      if (!isEmpty(value)) {
        const existingFilterIndex = filters.findIndex((filter) => filter.key === key); // find if object is exist in filter array
        if (existingFilterIndex !== -1) {
          const updatedFilters = [...filters];
          updatedFilters[existingFilterIndex] = {
            key,
            value,
            type: filterType || "string",
            nestedKey,
            label,
            dynamicLabel,
          }; // modify exist object
          setFilters(updatedFilters);
        } else {
          setFilters([...filters, { key, value, type: filterType || "string", nestedKey, label, dynamicLabel }]); // push new object
        }
      } else {
        handleFilterRemove(key);
      }

      setPagination(initialPagination);
    }, 500),
    [filters]
  );

  const handleFilterRemove = useCallback(
    (keyToRemove) => {
      const updatedFilters = filters.filter(({ key }) => key !== keyToRemove); // remove object from array
      setFilters(updatedFilters);
      setPagination(initialPagination);
    },
    [filters]
  );

  const getFormValueFromArray = filters.reduce((acc, { key, value }) => {
    acc[key] = value;
    return acc;
  }, {});

  //make filter's form field
  const filterField = filterFormFields
    ?.map((value) => {
      return {
        key: value.key,
        label: value.label,
        filter: (
          <CommonForm
            formRef={formRef}
            initialValues={{ [value.key]: getFormValueFromArray[value.key] || value?.initialValue }} // find value from array
            onFormChange={(e) => handleFilterChange(e, value)}
            formFields={value.formFields}
            isSave={false}
          />
        ),
        pinned: pinnedFilter.includes(value?.key),
      };
    })
    ?.filter((e) => !currentTabData?.hideFilter?.includes(e?.key));

  function isEmpty(value) {
    if (Array.isArray(value)) {
      return value.length === 0;
    } else {
      return value === "" || value == null;
    }
  }

  useEffect(() => {
    //make filter for shopify field
    const tempAppliedFilters = filters
      .filter((val) => !isEmpty(val.value) && !val?.tabQuery)
      .map(({ key, value, label, dynamicLabel }) => ({
        key,
        label: makeAdvanceFilterLabel(value, label, dynamicLabel),
        onRemove: () => handleFilterRemove(key),
      }));
    setAppliedFilters(tempAppliedFilters);
  }, [filters]);

  return (
    <Card style={{ marginBottom: "16px" }}>
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        {/* Search Input */}
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={16} md={18}>
            <Search
              placeholder="Search..."
              value={queryValue}
              onChange={(e) => handleFiltersQueryChange(e.target.value)}
              onSearch={handleFiltersQueryChange}
              allowClear
              prefix={<SearchOutlined />}
              size="middle"
            />
          </Col>
          <Col xs={24} sm={8} md={6}>
            <Space>
              {appliedFilters.length > 0 && (
                <Button icon={<ClearOutlined />} onClick={handleFiltersClearAll} size="middle">
                  Clear All
                </Button>
              )}
            </Space>
          </Col>
        </Row>

        {/* Filter Fields */}
        {filterField && filterField.length > 0 && (
          <Row gutter={[16, 16]}>
            {filterField.map((field) => (
              <Col xs={24} sm={12} md={8} lg={6} key={field.key}>
                <Space direction="vertical" size="small" style={{ width: "100%" }}>
                  <Text strong>{field.label}</Text>
                  {field.filter}
                </Space>
              </Col>
            ))}
          </Row>
        )}

        {/* Applied Filters */}
        {appliedFilters.length > 0 && (
          <Space wrap>
            <Text type="secondary">Applied filters:</Text>
            {appliedFilters.map((filter) => (
              <Tag key={filter.key} closable onClose={filter.onRemove} color="blue">
                {filter.label}
              </Tag>
            ))}
          </Space>
        )}
      </Space>
    </Card>
  );
}
